import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Box, CircularProgress, Alert } from '@mui/material';
import { LeaveEntry } from '@techgitter/contracts';
import { WorkItem } from '../components/workitems/workItemTypes';
import { AppointmentSidePanelDefaults } from '../components/calendar/AppointmentSidePanel';
import { useCalendarNavigation } from '../components/calendar/hooks/useCalendarNavigation';
import { useCalendarData } from '../components/calendar/hooks/useCalendarData';
import { useConflictDetection } from '../components/calendar/hooks/useConflictDetection';
import { usePublicHolidays } from '../hooks/usePublicHolidays';
import { useUsers } from '../hooks/useUsers';
import { CalendarToolbar } from '../components/calendar/CalendarToolbar';
import { CalendarDay } from '../components/calendar/CalendarDay';
import type { CalendarLeaveVM } from '../components/calendar/CalendarDay';
import type { CalendarAppointmentVM } from '../components/calendar/CalendarAppointment';
import { AppointmentSidePanel } from '../components/calendar/AppointmentSidePanel';
import { AppointmentDetailPopover } from '../components/calendar/AppointmentDetailPopover';
import { AddLeavePanel } from '../components/calendar/AddLeavePanel';
import { DebugPanel } from '../components/debug/DebugPanel';
import { installApiCallLogger } from '../services/apiCallLogger';
import type { DebugPanelConfig } from '../types/debugPanel';
import { getUsersCacheMetadata, invalidateUsersCache } from '../services/usersCacheService';
import {
  formatDayHeader,
  formatDateStr,
  isAppointmentOnDate,
  isLeaveOnDate,
} from '../components/calendar/utils/calendarUtils';
import './CalendarPage.css';

installApiCallLogger();

/** Local time-of-day label (HH:MM, 24h) for an appointment's start. */
function formatApptTime(startAt?: string): string {
  if (!startAt) return '';
  return new Date(startAt).toLocaleTimeString('en-AU', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

// TODO: Replace with auth context when available
const CURRENT_USER = 'Steve Thomas';

interface CalendarPageProps {
  /** When true, open the Schedule Appointment side panel immediately */
  openNewAppointment?: boolean;
}

export const CalendarPage: React.FC<CalendarPageProps> = ({ openNewAppointment }) => {
  const nav = useCalendarNavigation();
  const { holidays } = usePublicHolidays();
  const { users: cachedUsers } = useUsers();

  // Compute date range for data fetching
  const rangeStart = useMemo(() => {
    if (nav.viewMode === 'week') return nav.weekStart;
    const d = new Date(nav.currentDate);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [nav.viewMode, nav.weekStart, nav.currentDate]);

  const rangeEnd = useMemo(() => {
    if (nav.viewMode === 'week') {
      const d = new Date(nav.weekStart);
      d.setDate(d.getDate() + 4);
      return d;
    }
    return new Date(nav.currentDate);
  }, [nav.viewMode, nav.weekStart, nav.currentDate]);

  const { appointments, leaveEntries, technicianColumns, isLoading, error, reload } = useCalendarData({
    rangeStart,
    rangeEnd,
  });

  const { conflictMap } = useConflictDetection(appointments, leaveEntries, holidays);

  // Merge users from cache with technicians visible in calendar data for autocomplete
  const technicianNames = useMemo(() => {
    const names = new Set(cachedUsers.map(u => u.fullname));
    for (const col of technicianColumns) {
      if (col.userFullName && col.userFullName !== 'Unassigned') {
        names.add(col.userFullName);
      }
    }
    return Array.from(names).sort();
  }, [cachedUsers, technicianColumns]);

  // Side panel state
  const [sidePanelOpen, setSidePanelOpen] = useState(!!openNewAppointment);
  const [sidePanelDefaults, setSidePanelDefaults] = useState<AppointmentSidePanelDefaults | null>(null);

  // Detail popover state
  const [detailAnchor, setDetailAnchor] = useState<HTMLElement | null>(null);
  const [detailWorkItem, setDetailWorkItem] = useState<WorkItem | null>(null);

  // Right-panel state. Schedule Appointment and Add Leave share a single panel
  // column, so they're mutually exclusive.
  const [editingAppointment, setEditingAppointment] = useState<WorkItem | null>(null);
  const [leavePanelOpen, setLeavePanelOpen] = useState(false);
  const [editingLeave, setEditingLeave] = useState<LeaveEntry | null>(null);

  const handleAppointmentClick = useCallback((e: React.MouseEvent, workItem: WorkItem) => {
    setDetailAnchor(e.currentTarget as HTMLElement);
    setDetailWorkItem(workItem);
  }, []);

  const handleLeaveClick = useCallback((leave: LeaveEntry) => {
    setSidePanelOpen(false);
    setEditingAppointment(null);
    setEditingLeave(leave);
    setLeavePanelOpen(true);
  }, []);

  // Flatten the technician columns into per-day appointment + leave lists for
  // the V2 day-card layout. Appointments are merged across technicians and
  // sorted chronologically; each carries its technician name for display.
  const buildDayContent = useCallback(
    (dateStr: string): { appointments: CalendarAppointmentVM[]; leave: CalendarLeaveVM[] } => {
      const appointments: CalendarAppointmentVM[] = [];
      const leave: CalendarLeaveVM[] = [];
      for (const col of technicianColumns) {
        for (const appt of col.appointments) {
          if (isAppointmentOnDate(appt, dateStr)) {
            appointments.push({
              workItem: appt,
              technicianName: col.userFullName,
              title: appt.title,
              startTime: formatApptTime(appt.startAt),
            });
          }
        }
        const onLeave = col.leaveEntries.find((l) =>
          isLeaveOnDate(l.startAt, l.endAt, dateStr),
        );
        if (onLeave) leave.push({ leave: onLeave, technicianName: col.userFullName });
      }
      appointments.sort((a, b) =>
        (a.workItem.startAt ?? '').localeCompare(b.workItem.startAt ?? ''),
      );
      return { appointments, leave };
    },
    [technicianColumns],
  );

  const days = useMemo(() => {
    const todayStr = formatDateStr(new Date());
    const base =
      nav.viewMode === 'week'
        ? nav.weekDays.map((d) => ({ date: d.date, dateStr: d.dateStr, isToday: d.isToday }))
        : [
            {
              date: nav.currentDate,
              dateStr: nav.currentDateStr,
              isToday: nav.currentDateStr === todayStr,
            },
          ];
    return base.map((d) => {
      const { appointments, leave } = buildDayContent(d.dateStr);
      return {
        day: {
          date: d.date,
          dateStr: d.dateStr,
          isToday: d.isToday,
          label: formatDayHeader(d.date),
        },
        appointments,
        leave,
        holiday: holidays.find((h) => h.date === d.dateStr) ?? null,
      };
    });
  }, [nav.viewMode, nav.weekDays, nav.currentDate, nav.currentDateStr, holidays, buildDayContent]);

  const handleEditAppointment = useCallback((workItem: WorkItem) => {
    setDetailAnchor(null);
    setDetailWorkItem(null);
    setLeavePanelOpen(false);
    setEditingAppointment(workItem);
    setSidePanelDefaults(null);
    setSidePanelOpen(true);
  }, []);

  const handleAddAppointment = useCallback(() => {
    setLeavePanelOpen(false);
    setEditingAppointment(null);
    setSidePanelDefaults(null);
    setSidePanelOpen(true);
  }, []);

  const handleAddLeave = useCallback(() => {
    setSidePanelOpen(false);
    setEditingAppointment(null);
    setEditingLeave(null);
    setLeavePanelOpen(true);
  }, []);

  const handleSidePanelClose = useCallback(() => {
    setSidePanelOpen(false);
    setEditingAppointment(null);
  }, []);

  const handleLeavePanelClose = useCallback(() => {
    setLeavePanelOpen(false);
    setEditingLeave(null);
  }, []);

  const handleAppointmentCreated = useCallback(() => {
    setSidePanelOpen(false);
    // Data will refresh via SignalR, but reload as fallback
    reload();
  }, [reload]);

  // Escape closes whichever panel is open.
  const panelOpen = sidePanelOpen || leavePanelOpen;
  useEffect(() => {
    if (!panelOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (sidePanelOpen) handleSidePanelClose();
      else handleLeavePanelClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [panelOpen, sidePanelOpen, handleSidePanelClose, handleLeavePanelClose]);

  const dateLabel =
    nav.viewMode === 'week'
      ? nav.weekRangeLabel
      : formatDayHeader(nav.currentDate);

  // Debug panel config
  const debugConfig = useMemo<DebugPanelConfig>(() => ({
    sections: [
      { id: 'calendar', label: 'Calendar' },
    ],
    hookStates: [
      {
        name: 'useCalendarData',
        section: 'calendar',
        status: error ? 'error' : isLoading ? 'loading' : 'loaded',
        error,
        recordCount: appointments.length,
        detail: `${appointments.length} appointments, ${leaveEntries.length} leave entries`,
      },
      {
        name: 'usePublicHolidays',
        section: 'calendar',
        status: 'loaded',
        recordCount: holidays.length,
      },
      {
        name: 'useConflictDetection',
        section: 'calendar',
        status: 'loaded',
        recordCount: conflictMap.size,
        detail: `${conflictMap.size} appointments with conflicts`,
      },
    ],
    caches: [
      {
        name: 'Users',
        section: 'calendar',
        type: 'memory+localStorage',
        getRecordCount: () => getUsersCacheMetadata()?.recordCount ?? 0,
        getAge: () => {
          const meta = getUsersCacheMetadata();
          if (!meta) return null;
          const mins = Math.floor((Date.now() - meta.cachedAt) / 60_000);
          if (mins < 60) return `${mins}m`;
          return `${Math.floor(mins / 60)}h ${mins % 60}m`;
        },
        getSizeEstimate: () => {
          try {
            const raw = localStorage.getItem('users_cache_v1');
            if (!raw) return '0 KB';
            return `${Math.round(raw.length / 1024)} KB`;
          } catch { return 'unknown'; }
        },
        getData: () => getUsersCacheMetadata(),
        clear: () => { invalidateUsersCache(); },
      },
    ],
    dataSections: [],
  }), [appointments.length, leaveEntries.length, holidays.length, conflictMap.size, error, isLoading]);

  return (
    <div className="calendar-page">
      <CalendarToolbar
        viewMode={nav.viewMode}
        dateLabel={dateLabel}
        onPrev={nav.goPrev}
        onNext={nav.goNext}
        onToday={nav.goToToday}
        onViewModeChange={nav.setViewMode}
        onAddAppointment={handleAddAppointment}
        onAddLeave={handleAddLeave}
      />

      <div
        className={`calendar-layout ${
          panelOpen ? 'calendar-layout--panel-open' : 'calendar-layout--panel-closed'
        }`}
      >
        {/* Day list — floating card on the grey surface */}
        <div className="calendar-card">
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          )}

          {!isLoading && !error &&
            days.map((d) => (
              <CalendarDay
                key={d.day.dateStr}
                day={d.day}
                appointments={d.appointments}
                leave={d.leave}
                holiday={d.holiday}
                onAppointmentClick={handleAppointmentClick}
                onLeaveClick={handleLeaveClick}
              />
            ))}
        </div>

        {/* Right panel — Schedule Appointment or Add Leave (mutually exclusive) */}
        {sidePanelOpen && (
          <AppointmentSidePanel
            defaults={sidePanelDefaults}
            editingAppointment={editingAppointment}
            currentUser={CURRENT_USER}
            technicians={technicianNames}
            appointments={appointments}
            leaveEntries={leaveEntries}
            holidays={holidays}
            onClose={handleSidePanelClose}
            onCreated={handleAppointmentCreated}
          />
        )}
        {!sidePanelOpen && leavePanelOpen && (
          <AddLeavePanel
            editingLeave={editingLeave}
            onClose={handleLeavePanelClose}
            onSaved={reload}
          />
        )}
      </div>

      {/* Appointment detail popover */}
      <AppointmentDetailPopover
        anchorEl={detailAnchor}
        workItem={detailWorkItem}
        conflicts={detailWorkItem ? (conflictMap.get(detailWorkItem.guid) ?? []) : []}
        onClose={() => {
          setDetailAnchor(null);
          setDetailWorkItem(null);
        }}
        onEdit={handleEditAppointment}
      />

      {/* Debug panel */}
      <DebugPanel config={debugConfig} />
    </div>
  );
};
